import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { feedbackAPI } from '../services/api'
import 'bootstrap-icons/font/bootstrap-icons.css'

const STATUS_LABELS = {
  under_review: 'Under Review',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
}

const STATUS_STYLES = {
  under_review: 'bg-amber-400/20 text-amber-200 border-amber-400/40',
  in_progress: 'bg-blue-400/20 text-blue-200 border-blue-400/40',
  resolved: 'bg-emerald-400/20 text-emerald-200 border-emerald-400/40',
  closed: 'bg-gray-400/20 text-gray-200 border-gray-400/40',
}

const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${STATUS_STYLES[status] || STATUS_STYLES.under_review}`}>
    {STATUS_LABELS[status] || status}
  </span>
)

const formatTime = (iso) => {
  const d = new Date(iso)
  const now = new Date()
  const sameDay = d.toDateString() === now.toDateString()
  return sameDay
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

const Avatar = ({ isDeveloper }) => (
  <div className={`w-7 h-7 flex-shrink-0 rounded-full flex items-center justify-center text-xs ${
    isDeveloper ? 'bg-gray-800 text-kbl-neon-400' : 'bg-kbl-neon-500 text-black'
  }`}>
    <i className={`bi ${isDeveloper ? 'bi-headset' : 'bi-person-fill'}`}></i>
  </div>
)

const seenKey = (threadId) => `kbl_feedback_seen_${threadId}`

// Professional support-widget pattern (Intercom/Crisp/Zendesk style): a floating
// action button, fixed bottom-right on every page, instead of a header nav item.
// Superusers manage every conversation from the full Developer Inbox page instead
// of a small popup - a multi-conversation inbox doesn't fit a chat bubble.
const FeedbackWidget = () => {
  const { isAuthenticated, isSuperuser } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [thread, setThread] = useState(null)
  const [loading, setLoading] = useState(true)
  const [newMessage, setNewMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const [hasUnread, setHasUnread] = useState(false)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  const loadThread = useCallback(async (silent = false, currentlyOpen = false) => {
    if (!silent) setLoading(true)
    try {
      const res = await feedbackAPI.getMyThread()
      const data = res.data.data
      setThread(data)
      setError(null)

      if (!data) {
        setHasUnread(false)
      } else {
        const seen = parseInt(localStorage.getItem(seenKey(data.id)) || '0', 10)
        const count = data.messages?.length || 0
        if (currentlyOpen) {
          localStorage.setItem(seenKey(data.id), String(count))
          setHasUnread(false)
        } else {
          setHasUnread(count > seen)
        }
      }
    } catch (err) {
      setError('Failed to load your conversation.')
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!isAuthenticated || isSuperuser) return
    loadThread(false, false)
    const interval = setInterval(() => loadThread(true, open), 10000)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isSuperuser, open])

  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      inputRef.current?.focus()
      if (thread) {
        localStorage.setItem(seenKey(thread.id), String(thread.messages?.length || 0))
        setHasUnread(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, thread?.messages?.length])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open])

  if (!isAuthenticated) return null

  if (isSuperuser) {
    return (
      <button
        onClick={() => navigate('/to-developer')}
        title="Developer Inbox"
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-kbl-neon-500 to-kbl-neon-600 text-black shadow-xl hover:shadow-glow-neon hover:scale-110 active:scale-95 transition-all duration-300 flex items-center justify-center"
      >
        <i className="bi bi-headset text-2xl"></i>
      </button>
    )
  }

  const isClosed = thread && (thread.status === 'resolved' || thread.status === 'closed')

  const handleSend = async (e) => {
    e.preventDefault()
    const body = newMessage.trim()
    if (!body || sending) return
    setSending(true)
    setNewMessage('')
    try {
      let activeThread = thread
      if (!activeThread) {
        const res = await feedbackAPI.startNewThread()
        activeThread = res.data.data
      }
      await feedbackAPI.postMessage(activeThread.id, body)
      await loadThread(true, true)
    } catch (err) {
      setError('Failed to send message.')
      setNewMessage(body)
    } finally {
      setSending(false)
      inputRef.current?.focus()
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend(e)
    }
  }

  const handleStartNew = async () => {
    try {
      await feedbackAPI.startNewThread()
      await loadThread(false, true)
    } catch (err) {
      setError('Failed to start a new conversation.')
    }
  }

  return (
    <>
      <div
        role="dialog"
        aria-label="Talk to the Developer"
        className={`fixed bottom-24 right-6 z-40 w-[calc(100vw-3rem)] sm:w-[380px] h-[70vh] sm:h-[540px] max-h-[600px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden origin-bottom-right transition-all duration-200 ease-out ${
          open ? 'scale-100 opacity-100 translate-y-0 pointer-events-auto' : 'scale-95 opacity-0 translate-y-2 pointer-events-none'
        }`}
      >
        {/* Header */}
        <div className="px-5 py-4 bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-start justify-between flex-shrink-0 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-kbl-neon-500 to-transparent opacity-60"></div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-kbl-neon-500/20 to-kbl-neon-600/20 border-2 border-kbl-neon-500/50 flex items-center justify-center">
              <i className="bi bi-headset text-kbl-neon-400 text-lg"></i>
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm leading-tight">Talk to the Developer</h3>
              <p className="text-gray-400 text-[11px] mt-0.5">We usually reply within a few hours</p>
            </div>
          </div>
          <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-white transition-colors mt-0.5">
            <i className="bi bi-x-lg text-lg"></i>
          </button>
        </div>
        {thread && (
          <div className="px-5 py-2 bg-gray-50 border-b border-gray-200 flex-shrink-0">
            <StatusBadge status={thread.status} />
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 bg-gray-50">
          {loading ? (
            <div className="space-y-3 animate-pulse">
              <div className="flex justify-start"><div className="w-2/3 h-10 bg-gray-200 rounded-2xl"></div></div>
              <div className="flex justify-end"><div className="w-1/2 h-10 bg-gray-200 rounded-2xl"></div></div>
              <div className="flex justify-start"><div className="w-3/5 h-10 bg-gray-200 rounded-2xl"></div></div>
            </div>
          ) : thread?.messages?.length ? (
            <>
              {thread.messages.map((msg, i) => {
                const prev = thread.messages[i - 1]
                const grouped = prev && prev.is_developer_reply === msg.is_developer_reply
                return (
                  <div
                    key={msg.id}
                    className={`flex items-end gap-2 ${msg.is_developer_reply ? 'justify-start' : 'justify-end'} ${grouped ? 'mt-1' : 'mt-3'}`}
                  >
                    {msg.is_developer_reply && (grouped ? <div className="w-7" /> : <Avatar isDeveloper />)}
                    <div className="max-w-[76%] group">
                      <div className={`rounded-2xl px-3.5 py-2 text-sm shadow-sm ${
                        msg.is_developer_reply
                          ? 'bg-white text-gray-900 rounded-bl-sm border border-gray-200'
                          : 'bg-gradient-to-r from-kbl-neon-500 to-kbl-neon-600 text-black rounded-br-sm'
                      }`}>
                        <div className="whitespace-pre-wrap break-words">{msg.body}</div>
                      </div>
                      <div className={`text-[10px] text-gray-400 mt-0.5 px-1 ${msg.is_developer_reply ? 'text-left' : 'text-right'}`}>
                        {formatTime(msg.created_at)}
                      </div>
                    </div>
                    {!msg.is_developer_reply && (grouped ? <div className="w-7" /> : <Avatar isDeveloper={false} />)}
                  </div>
                )
              })}
              <div ref={messagesEndRef} />
            </>
          ) : (
            <div className="text-center text-gray-400 mt-10 px-4">
              <i className="bi bi-chat-square-heart text-4xl mb-3 block text-gray-300"></i>
              <p className="text-sm font-medium text-gray-500">How can we help?</p>
              <p className="text-xs mt-1">Send us a message and we'll get back to you as soon as we can.</p>
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="px-3 py-3 border-t border-gray-200 bg-white flex-shrink-0">
          {isClosed ? (
            <button
              onClick={handleStartNew}
              className="w-full px-4 py-2.5 text-sm font-semibold rounded-xl bg-gradient-to-r from-kbl-neon-500 to-kbl-neon-600 text-black hover:shadow-glow-neon transition-all duration-200"
            >
              Start New Conversation
            </button>
          ) : (
            <form onSubmit={handleSend} className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type a message..."
                rows={1}
                className="flex-1 px-3 py-2.5 text-sm border border-gray-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-kbl-neon-500/50 focus:border-kbl-neon-500 max-h-24"
                disabled={sending}
              />
              <button
                type="submit"
                disabled={sending || !newMessage.trim()}
                className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-gradient-to-r from-kbl-neon-500 to-kbl-neon-600 text-black disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-glow-neon transition-all duration-200"
              >
                <i className={`bi ${sending ? 'bi-arrow-repeat animate-spin' : 'bi-send'}`}></i>
              </button>
            </form>
          )}
          {error && <p className="text-xs text-red-600 mt-1.5">{error}</p>}
        </div>
      </div>

      <button
        onClick={() => setOpen((o) => !o)}
        title="Talk to the Developer"
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-kbl-neon-500 to-kbl-neon-600 text-black shadow-xl hover:shadow-glow-neon hover:scale-110 active:scale-95 transition-all duration-300 flex items-center justify-center"
      >
        <i className={`bi ${open ? 'bi-x-lg' : 'bi-chat-dots-fill'} text-2xl`}></i>
        {hasUnread && !open && (
          <span className="absolute top-0 right-0 w-3.5 h-3.5 bg-red-500 border-2 border-white rounded-full animate-pulse"></span>
        )}
      </button>
    </>
  )
}

export default FeedbackWidget
